// src/components/PaintMatcher/PaintMatcher.js (Fixed Brand Filter)
import React, { useState, useEffect } from 'react';
import './PaintMatcher.css';
import ImageUploader from '../ImageUploader/ImageUploader';
import PaletteDisplay from '../PaletteDisplay/PaletteDisplay';
import MatchResults from '../MatchResults/MatchResults';
import ShoppingList from '../ShoppingList/ShoppingList';
import BrandFilter from '../BrandFilter/BrandFilter';
import PresetSelector from '../PresetSelector/PresetSelector';
import usePalette from '../../utils/usePalette';
import { PRESET_OPTIONS } from '../../utils/presets';

const API_BASE = (process.env.REACT_APP_MATCH_API || 'https://k6aslecb84.execute-api.us-west-2.amazonaws.com/prod').replace(/\/$/, '');

// Auto-detect preset based on image analysis
async function choosePresetForImage(file) {
  const url = URL.createObjectURL(file);
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });

    const maxDim = 200;
    const scale = Math.min(maxDim / img.width, maxDim / img.height);
    canvas.width = img.width * scale;
    canvas.height = img.height * scale;
    
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    let whites = 0, blacks = 0, blueish = 0;
    const pixels = data.length / 4;
    
    for (let i = 0; i < data.length; i += 4) {
      const [r, g, b] = [data[i], data[i + 1], data[i + 2]];
      
      if (r >= 240 && g >= 240 && b >= 240) whites++;
      if (r <= 5 && g <= 5 && b <= 5) blacks++;
      if (b > 80 && b > r + 10 && b > g + 10) blueish++;
    }

    const fracWhite = whites / Math.max(1, pixels);
    const fracBlack = blacks / Math.max(1, pixels);
    const fracBlue = blueish / Math.max(1, pixels);

    if (fracWhite > 0.35) return "whiteBackdrop";
    if (fracBlue > 0.02 || (fracBlack > 0.2 && fracBlue > 0.005)) return "darkNavies";
    return "general";
  } finally {
    URL.revokeObjectURL(url);
  }
}

const PaintMatcher = () => {
  const [k, setK] = useState(9);
  const [presetKey, setPresetKey] = useState(() => localStorage.getItem("presetKey") || "auto");
  const [selectedBrands, setSelectedBrands] = useState(() => {
    const saved = localStorage.getItem("selectedBrands");
    return saved ? JSON.parse(saved) : ["Citadel", "Vallejo", "Army Painter", "AK Interactive"];
  });
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  
  const { extract, previewUrl, palette, loading } = usePalette();

  // Save preferences to localStorage
  useEffect(() => {
    localStorage.setItem("presetKey", presetKey);
  }, [presetKey]);

  useEffect(() => {
    localStorage.setItem("selectedBrands", JSON.stringify(selectedBrands));
  }, [selectedBrands]);

  const onFileSelect = async (file) => {
    if (!file) return;
    
    setUploadedImage(file.name);
    setMatches([]);
    setError(null);

    let keyToUse = presetKey;
    if (presetKey === "auto") {
      keyToUse = await choosePresetForImage(file);
    }
    const options = PRESET_OPTIONS[keyToUse] || PRESET_OPTIONS.general;

    await extract(file, k, options);
  };

  const fetchMatches = async () => {
    if (!palette.length) return;
    if (selectedBrands.length === 0) {
      setError("Please select at least one paint brand to search.");
      return;
    }

    setBusy(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_BASE}/match-hex`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ 
          hexes: palette, 
          topN: 3,
          brands: selectedBrands // Send selected brands to API
        }),
      });
      
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`API ${res.status}: ${text}`);
      }
      
      const json = await res.json();
      setMatches(json.results || []);
    } catch (err) {
      console.error("Match API error:", err);
      setError("Unable to connect to the paint matching service. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Filter matches to only show paints from selected brands
  const filteredMatches = matches.map(match => ({
    ...match,
    matches: match.matches?.filter(paint => selectedBrands.includes(paint.brand)) || []
  })).filter(match => match.matches.length > 0); // Remove color matches with no valid paints

  // Build deduped shopping list from filtered matches
  const shoppingSet = new Map();
  for (const r of filteredMatches) {
    const best = r.matches?.[0]; // Already filtered, so just take the best match
    if (best) {
      shoppingSet.set(best.slug || best.name, best);
    }
  }
  const shoppingList = Array.from(shoppingSet.values()).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <main 
      id="paint-matcher" 
      className="paint-matcher"
      role="main"
      aria-labelledby="matcher-heading"
      itemScope
      itemType="https://schema.org/WebApplication"
    >
      <div className="container">
        <header className="section-header">
          <h2 
            id="matcher-heading"
            className="section-title"
            itemProp="name"
          >
            AI Paint Color Matcher
          </h2>
          <p 
            className="section-subtitle"
            itemProp="description"
          >
            Upload your image and find the perfect paint matches using advanced color science
          </p>
        </header>

        <div className="matcher-grid">
          <aside 
            className="upload-section"
            aria-labelledby="upload-heading"
          >
            <div className="upload-controls">
              <h3 id="upload-heading" className="sr-only">Upload and Settings</h3>
              
              <div role="group" aria-labelledby="image-upload-label">
                <ImageUploader 
                  onFileSelect={onFileSelect} 
                  previewUrl={previewUrl}
                  aria-describedby="upload-instructions" 
                />
                <p id="upload-instructions" className="sr-only">
                  Upload a photo of your miniature to extract colors and find matching hobby paints from Citadel, Vallejo, Army Painter and other brands
                </p>
              </div>
              
              <div className="controls" role="group" aria-labelledby="settings-heading">
                <h4 id="settings-heading" className="sr-only">Color Extraction Settings</h4>
                
                <button
                  onClick={fetchMatches}
                  disabled={!palette.length || loading || busy || selectedBrands.length === 0}
                  className="match-button"
                  aria-describedby="match-status"
                  type="button"
                >
                  {busy ? (
                    <>
                      <span className="spinner" aria-hidden="true" role="presentation" />
                      <span>Finding Matches...</span>
                    </>
                  ) : (
                    <>
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        aria-hidden="true"
                        role="presentation"
                      >
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                      </svg>
                      <span>Match Paints</span>
                    </>
                  )}
                </button>

                <div className="control-group">
                  <label htmlFor="k-value" className="control-label">
                    Number of Colors (k): <span className="range-value">{k}</span>
                  </label>
                  <input
                    id="k-value"
                    type="range"
                    min="3"
                    max="12"
                    value={k}
                    onChange={(e) => setK(parseInt(e.target.value, 10))}
                    className="range-input"
                    aria-describedby="k-value-help"
                  />
                  <p id="k-value-help" className="sr-only">
                    Choose how many dominant colors to extract from your image. More colors provide more paint options but may include less important details.
                  </p>
                </div>

                <div className="control-group">
                  <label htmlFor="preset-selector" className="control-label">
                    Image Type Preset
                  </label>
                  <PresetSelector 
                    id="preset-selector"
                    value={presetKey} 
                    onChange={setPresetKey}
                    aria-describedby="preset-help"
                  />
                  <p id="preset-help" className="sr-only">
                    Select the preset that best matches your image type for optimal color extraction results.
                  </p>
                </div>

                <div className="control-group">
                  <BrandFilter 
                    selectedBrands={selectedBrands}
                    onChange={setSelectedBrands}
                  />
                </div>

                {error && (
                  <div 
                    className="error-message" 
                    role="alert"
                    aria-live="polite"
                  >
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      aria-hidden="true"
                      role="presentation"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    {error}
                  </div>
                )}

                <div id="match-status" className="sr-only" aria-live="polite">
                  {uploadedImage && `Image uploaded: ${uploadedImage}. `}
                  {palette.length > 0 && `Extracted ${palette.length} colors. `}
                  {filteredMatches.length > 0 && `Found ${filteredMatches.length} paint matches.`}
                </div>
              </div>
            </div>
          </aside>

          <div className="results-section">
            <PaletteDisplay palette={palette} loading={loading} />
            
            {filteredMatches.length > 0 && (
              <>
                <MatchResults matches={filteredMatches} />
                <ShoppingList shoppingList={shoppingList} />
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default PaintMatcher;